import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import React from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Organisation, useListOrganisations } from '../../../app/ApiGen';
import { useAuthenticatedUser } from '../../../app/AuthenticatedUserProvider';
import { labelsFor, OrganisationTier } from '../../../app/enums';
import { CacheAwareRefreshButton } from '../../../components/DataTable/CacheRefreshButton';
import { ColumnFiltersToggle } from '../../../components/DataTable/Toolbar';
import { DataErrorHandler } from '../../../components/ErrorHandler';
import { ExportCsv, ExportProps } from '../../../components/ExportCsv';
import { TableLink } from '../../../components/Link';
import {
  ColumnChanger,
  ColumnChangerProps,
  DataTable,
  SearchField,
  SearchFieldProps,
  Workspace,
} from '../../../design-system';
import type { Column } from '../../../utils/dataTable/columns';
import { navigateOnRowClick } from '../../../utils/dataTable/rowHelpers';
import { getCondensedDate } from '../../../utils/dateFormat';
import { filterBySelectableItem } from '../../../utils/jobs/filterComponents/SelectableFilters';
import { TextFieldFilter } from '../../../utils/jobs/filterComponents/TextFieldFilter';
import { YesNoFilters, type YesNoOption } from '../../../utils/jobs/filterComponents/YesNoFilters';
import { normalise, Normalised } from '../../../utils/request';
import { sortByLocale } from '../../../utils/sortBy';
import OrganisationReportButton from '../../Organisations/Organisation/OrganisationReportButton';
import { getRoleInOrganisation } from './util';

type TableOrganisation = Omit<Normalised<Organisation>, 'role' | 'tier'> & { role: string; tier: string };

const getOrganisationUrl = (organisation: TableOrganisation) => `/admin/organisations/${organisation.slug}`;

const csvTitle = 'organisations';

export const List: React.FC = () => {
  const [filtering, setFiltering] = React.useState(false);

  const history = useHistory();
  const { loading, error, data, refetch } = useListOrganisations({});
  const { authenticatedUser } = useAuthenticatedUser();

  const handleRefresh = () => {
    refetch({ queryParams: { forceRefresh: true } }).catch(() => {
      console.log('Error refreshing organisations');
    });
  };

  if (error) {
    return <DataErrorHandler refetch={refetch} description="Unable to load organisations" error={error} />;
  }

  const organisations = data ? normalise(data) : [];
  const tableOrganisations = organisations.map((organisation) => ({
    ...organisation,
    role: getRoleInOrganisation(authenticatedUser, organisation.id),
    tier: labelsFor(OrganisationTier)[organisation.tier],
  }));

  const cacheInfo = data?.cache as { lastUpdated?: string } | undefined;

  const columns: Column<TableOrganisation>[] = [
    {
      field: 'name',
      title: 'Name',
      defaultSort: 'asc',
      customSort: sortByLocale('name'),
      render: (organisation) => <TableLink to={getOrganisationUrl(organisation)}>{organisation.name}</TableLink>,
    },
    { field: 'tier', title: 'Tier' },
    { field: 'role', title: 'Role' },
    { field: 'chargePoints.length' as 'chargePoints', title: 'Charge Points' },
    { field: 'slug', title: 'Slug', hidden: true },
    { field: 'id', title: 'Organisation ID', hidden: true },
    {
      field: 'allowLongTermOfflineCharging',
      title: 'Long term offline charging',
      hidden: true,
      render: ({ allowLongTermOfflineCharging }) => (allowLongTermOfflineCharging ? 'Yes' : 'No'),
      filterComponent: YesNoFilters,
      customFilterAndSearch: (search: unknown, { allowLongTermOfflineCharging }) =>
        filterBySelectableItem<YesNoOption>(search, { value: allowLongTermOfflineCharging ? 'Yes' : 'No' }),
    },
    {
      field: 'created',
      title: 'Created',
      hidden: true,
      render: ({ created }) => getCondensedDate(created),
      filterComponent: TextFieldFilter,
      customFilterAndSearch: (search: unknown, { created }) => {
        if (typeof search !== 'string' || search.trim() === '') return true;
        return getCondensedDate(created).toLowerCase().includes(search.toLowerCase());
      },
    },
  ];

  return (
    <Workspace maxWidth="xl">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card>
            <DataTable<TableOrganisation>
              options={{ columnsButton: true, filtering }}
              toolbarProps={{
                search: ({ dataManager, search, onSearchChanged }: SearchFieldProps) => (
                  <SearchField
                    dataManager={dataManager}
                    item="organisations"
                    onSearchChanged={onSearchChanged}
                    search={search}
                  />
                ),
                actions: (props: React.PropsWithChildren<ColumnChangerProps> & ExportProps<TableOrganisation>) => (
                  <>
                    <ColumnChanger
                      columnsButton={props.columnsButton}
                      columns={props.columns}
                      icons={props.icons}
                      onColumnsChanged={props.onColumnsChanged}
                    />
                    <ColumnFiltersToggle filtering={filtering} setFiltering={setFiltering} />
                    <CacheAwareRefreshButton onRefresh={handleRefresh} isLoading={loading} cacheInfo={cacheInfo} />
                    <ExportCsv<TableOrganisation>
                      columns={props.columns}
                      data={props.data}
                      exportFileName={csvTitle}
                      getFieldValue={props.getFieldValue}
                      icons={props.icons}
                    />
                    <Button color="primary" component={Link} to="/admin/create/organisation" variant="contained">
                      Create organisation
                    </Button>
                  </>
                ),
              }}
              columns={columns}
              data={tableOrganisations}
              onRowClick={navigateOnRowClick((organisation) => getOrganisationUrl(organisation), history)}
            />
          </Card>
        </Grid>
        {!loading && (
          <Grid item xs={12}>
            <OrganisationReportButton organisations={organisations} />
          </Grid>
        )}
      </Grid>
    </Workspace>
  );
};
